// src/components/InlineReplyComposer.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Send,
  MessageCircle,
  Bot,
  Sparkles,
  X,
  Mail,
  RefreshCw,
  Check,
  AlertTriangle,
  Phone,
  HelpCircle,
  UserX,
  FileText
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../firebase';
import { doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { injectNameIntoScript, normalizeScriptWithPlaceholder, stripSignOff } from '../utils/emailPreview';
import { safeGreetingFirstName } from '../utils/greetingName';
import { validateOutboundDraft } from '../utils/draftGuards';
import { prepareActionAuditOutboxBatch } from '../utils/actionAudit';
import { getTourActionLabel, hasSuggestedEmailRecipient } from '../utils/actionNotifications';
import { getActionCardAttachments, getActionCardDetails, getNeedsUserInputLabel } from '../utils/actionCardDetails';
import { getHoldFraming } from '../utils/holdFraming';
import { getThreadDisplaySubject } from '../utils/threadGrouping';
import { buildActiveSignatureFromProfile } from '../utils/signatureBuilder';
import SignaturePreview from './SignaturePreview';
import '../styles/InlineReplyComposer.css';

function getVisibleThreadAnchor(thread) {
  const displaySubject = getThreadDisplaySubject(thread?.subject || '');
  return displaySubject && displaySubject !== 'Unknown' ? displaySubject : null;
}

function getReplyRowAnchor(thread, notification) {
  return getVisibleThreadAnchor(thread) || notification?.rowAnchor || null;
}

function getReplySubject(thread, notification, suggestedEmail) {
  const threadAnchor = getVisibleThreadAnchor(thread);
  if (threadAnchor) return `Re: ${threadAnchor}`;
  if (suggestedEmail?.subject) return suggestedEmail.subject;
  if (notification?.rowAnchor) return `Re: ${notification.rowAnchor}`;
  return '';
}

function getRecipientAddress(value) {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'object') {
    return (
      value.email ||
      value.address ||
      value.emailAddress?.address ||
      ''
    ).trim();
  }
  return '';
}

function normalizeRecipientList(value) {
  const rawValues = Array.isArray(value) ? value : [value];
  const seen = new Set();
  const recipients = [];

  rawValues.flatMap(item => Array.isArray(item) ? item : [item]).forEach(item => {
    const address = getRecipientAddress(item);
    if (!address) return;
    const key = address.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    recipients.push(address);
  });

  return recipients;
}

function getSuggestedCcRecipients(meta = {}, toRecipients = []) {
  const sourceMessage = meta.sourceMessage || {};
  const rawCcValues = [
    meta.ccEmails,
    meta.ccRecipients,
    meta.suggestedEmail?.cc,
    meta.suggestedEmail?.ccEmails,
    meta.suggestedEmail?.ccRecipients,
    sourceMessage.cc,
    sourceMessage.ccEmails,
    sourceMessage.ccRecipients
  ];
  const toSet = new Set(normalizeRecipientList(toRecipients).map(email => email.toLowerCase()));
  return normalizeRecipientList(rawCcValues).filter(email => !toSet.has(email.toLowerCase()));
}

function getSourceMessageIdentity(meta = {}) {
  const sourceMessage = meta.sourceMessage || {};
  const replyToMessageId = [
    meta.replyToMessageId,
    meta.sourceGraphMessageId,
    meta.sourceMessageId,
    sourceMessage.graphMessageId
  ].find(value => typeof value === 'string' && value.trim())?.trim() || null;

  return {
    replyToMessageId,
    sourceMessageId: meta.sourceMessageId || replyToMessageId,
    sourceGraphMessageId: meta.sourceGraphMessageId || sourceMessage.graphMessageId || replyToMessageId,
    sourceInternetMessageId: meta.sourceInternetMessageId || sourceMessage.internetMessageId || null,
    sourceMessage: Object.keys(sourceMessage).length ? sourceMessage : null
  };
}

function getMatchingSourceThread(thread, notification) {
  const sourceThreads = Array.isArray(thread?.sourceThreads) ? thread.sourceThreads : [];
  const notificationThreadId = notification?.threadId;
  if (notificationThreadId) {
    const matchingThread = sourceThreads.find(sourceThread => (
      sourceThread?.id === notificationThreadId ||
      sourceThread?.threadIds?.includes(notificationThreadId)
    ));
    if (matchingThread) return matchingThread;
  }
  return sourceThreads.length === 1 ? sourceThreads[0] : null;
}

function getMessageText(message = {}) {
  const body = message.body;
  const bodyText = typeof body === 'string'
    ? body
    : body?.content || body?.preview || '';
  const rawText = bodyText || message.text || message.content || '';
  return String(rawText)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p\s*>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .trim();
}

function cleanContactName(name) {
  if (!name) return null;
  return String(name).replace(/\d+/g, '').replace(/\s+/g, ' ').trim() || null;
}

function normalizeIdentityPart(value) {
  return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Inline reply composer for paused conversations.
 * Shows at the bottom of expanded threads that require user input.
 */
export default function InlineReplyComposer({
  thread,
  notification,
  client,
  onSend,
  onDismiss,
  onResolve,
  sendDisabledReason = ''
}) {
  const textareaRef = useRef();
  const initializedNotificationIdRef = useRef(null);
  const chatInputRef = useRef();
  const chatMessagesRef = useRef();

  // Email state
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [ccRecipients, setCcRecipients] = useState([]);
  const [emailSignature, setEmailSignature] = useState(null);
  const [signatureMode, setSignatureMode] = useState(null);
  const [professionalSignature, setProfessionalSignature] = useState(null);

  // AI Chat state
  const [showAiChat, setShowAiChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [showEmailUpdatedBadge, setShowEmailUpdatedBadge] = useState(false);

  // Send state - use ref to prevent double submission across re-renders
  const [isSending, setIsSending] = useState(false);
  const [draftErrors, setDraftErrors] = useState([]);
  const sendInProgressRef = useRef(false);

  // Get notification context - memoize to avoid dependency issues
  const meta = useMemo(() => notification?.meta || {}, [notification?.meta]);
  const reason = meta.reason || '';
  const deadLetterId = meta.deadLetterId || null;
  const isNeedsUserInput = reason.startsWith('needs_user_input:');
  const isCallRequested = reason === 'call_requested';
  const isWrongContact = reason.startsWith('wrong_contact:');
  const needsRedirectRecipient = isWrongContact && !hasSuggestedEmailRecipient(notification);
  const isPropertyIssue = reason.startsWith('property_issue:');
  const canMarkHandled = Boolean(onResolve && notification?.id);
  const sourceThread = useMemo(
    () => getMatchingSourceThread(thread, notification),
    [thread, notification]
  );
  const identityThread = sourceThread || thread;
  const replyRowNumber = sourceThread?.rowNumber
    ?? meta.rowNumber
    ?? notification?.rowNumber
    ?? thread?.rowNumber
    ?? null;
  const replyRowAnchor = useMemo(
    () => getReplyRowAnchor(thread, notification),
    [thread, notification]
  );

  // Get the broker's full name for [NAME] replacement
  const brokerFullName = useMemo(() => {
    if (isWrongContact) {
      const redirectedName = meta.suggestedEmail?.contactName || meta.suggestedContact;
      const cleaned = cleanContactName(redirectedName);
      if (cleaned) return cleaned;
    }

    // Helper to get broker name from thread messages
    const getNameFromMessages = (messages) => {
      if (!messages?.length) return null;
      for (let i = messages.length - 1; i >= 0; i--) {
        const msg = messages[i];
        if (msg.direction === 'inbound' && msg.from) {
          // msg.from could be "Mike Smith" or "mike@example.com" or "Mike Smith <mike@example.com>"
          let senderName = msg.from;
          // Remove email if present: "Mike Smith <mike@example.com>" -> "Mike Smith"
          if (senderName.includes('<')) {
            senderName = senderName.split('<')[0].trim();
          }
          // If it's just an email, skip to next source
          if (!senderName.includes('@')) {
            const cleaned = senderName.replace(/\d+/g, '').replace(/\s+/g, ' ').trim();
            if (cleaned) return cleaned;
          }
        }
      }
      return null;
    };

    const getNameFromPriorOutboundGreeting = (messages) => {
      if (!messages?.length) return null;
      const genericGreetings = new Set([
        'there',
        'team',
        'leasing team',
        'leasing agent',
        'broker',
        'sir/madam',
        'sir or madam'
      ]);

      for (let i = messages.length - 1; i >= 0; i--) {
        const msg = messages[i];
        if (msg.direction !== 'outbound') continue;

        const text = getMessageText(msg);

        const match = text.match(/^\s*(?:hi|hello|dear)\s+([^,\n]+),/i);
        if (!match) continue;

        const candidate = match[1].replace(/\s+/g, ' ').trim();
        const normalized = candidate.toLowerCase();
        if (
          !candidate ||
          candidate.includes('[') ||
          /[\d@]/.test(candidate) ||
          genericGreetings.has(normalized)
        ) {
          continue;
        }

        const cleaned = cleanContactName(candidate);
        if (cleaned) return cleaned;
      }

      return null;
    };

    // The exact prior outbound greeting preserves an intentional first-name
    // override even when the source thread also carries a fuller canonical name.
    const outboundGreetingName = getNameFromPriorOutboundGreeting(identityThread?.messages);
    if (outboundGreetingName) return outboundGreetingName;

    // The clicked notification's source thread is authoritative inside a
    // grouped property conversation, especially for shared mailboxes.
    if (sourceThread?.contactName) {
      const cleaned = cleanContactName(sourceThread.contactName);
      if (cleaned) return cleaned;
    }

    // Notification metadata is the next most specific identity source.
    if (meta.contactName) {
      const cleaned = cleanContactName(meta.contactName);
      if (cleaned) return cleaned;
    }

    const threadName = getNameFromMessages(identityThread?.messages);
    if (threadName) return threadName;

    // The final mailbox-map fallback is safe only when normalized grouped
    // source identities and map entries do not disagree.
    const groupedContactNames = new Map();
    (Array.isArray(thread?.sourceThreads) ? thread.sourceThreads : []).forEach(groupedSource => {
      const cleaned = cleanContactName(groupedSource?.contactName);
      const normalized = normalizeIdentityPart(cleaned);
      if (normalized && !groupedContactNames.has(normalized)) {
        groupedContactNames.set(normalized, cleaned);
      }
    });
    if (groupedContactNames.size > 1) return null;

    const identityEmails = [sourceThread?.email, notification?.email, thread?.email]
      .flat()
      .map(normalizeIdentityPart)
      .filter(Boolean);
    const identityEmailSet = new Set(identityEmails);
    const mappedContactNames = new Map();
    Object.entries(client?.emailToContactMap || {}).forEach(([email, contactName]) => {
      if (!identityEmailSet.has(normalizeIdentityPart(email))) return;
      const cleaned = cleanContactName(contactName);
      const normalized = normalizeIdentityPart(cleaned);
      if (normalized && !mappedContactNames.has(normalized)) {
        mappedContactNames.set(normalized, cleaned);
      }
    });
    const combinedContactNames = new Set([
      ...groupedContactNames.keys(),
      ...mappedContactNames.keys()
    ]);
    if (mappedContactNames.size === 1 && combinedContactNames.size === 1) {
      return mappedContactNames.values().next().value;
    }

    return null;
  }, [isWrongContact, meta.suggestedEmail, meta.suggestedContact, meta.contactName, notification?.email, sourceThread, identityThread?.messages, thread?.email, thread?.sourceThreads, client?.emailToContactMap]);

  const canonicalContactName = useMemo(() => {
    if (isWrongContact) {
      const redirectedName = meta.suggestedEmail?.contactName || meta.suggestedContact;
      const cleaned = cleanContactName(redirectedName);
      if (cleaned) return cleaned;
    }

    const sourceContactName = cleanContactName(sourceThread?.contactName);
    if (sourceContactName) return sourceContactName;

    const notificationContactName = cleanContactName(meta.contactName);
    if (notificationContactName) return notificationContactName;

    return brokerFullName;
  }, [isWrongContact, meta.suggestedEmail, meta.suggestedContact, meta.contactName, sourceThread?.contactName, brokerFullName]);

  // Get first name for the composer's placeholder hint, under the one shared
  // greeting rule rather than "everything before the first space" — the hint
  // proposes the opening line the operator is about to type.
  const contactFirstName = safeGreetingFirstName(brokerFullName);

  // Who is speaking on this card: the broker (five reasons) or SiteSift about
  // its own hold (the other twenty-nine, and anything unrecognised). Decided
  // once, in one module, for every surface that words a card (R18 D2).
  const holdFraming = useMemo(() => getHoldFraming(meta), [meta]);

  // Get reason label and icon
  const getReasonInfo = () => {
    if (reason === 'reply_review_required') return { icon: MessageCircle, label: 'Unsent Reply Draft', color: '#f59e0b' };
    const tourActionLabel = getTourActionLabel(reason);
    if (tourActionLabel) return { icon: MessageCircle, label: tourActionLabel, color: '#2563eb' };
    if (isNeedsUserInput) {
      const subReason = reason.split(':')[1] || '';
      if (subReason === 'confidential') return { icon: UserX, label: 'Identity Question', color: '#f59e0b' };
      if (subReason === 'client_question') return { icon: HelpCircle, label: 'Requirements Question', color: '#3b82f6' };
      if (subReason === 'negotiation') return { icon: FileText, label: 'Negotiation Request', color: '#8b5cf6' };
      if (subReason === 'legal_contract') return { icon: FileText, label: 'Contract Question', color: '#ef4444' };
      // Naming the reason is the difference between "something needs you" and
      // "the flyer could not be saved" (DEF-11).
      const mappedLabel = getNeedsUserInputLabel(reason);
      if (mappedLabel) return { icon: MessageCircle, label: mappedLabel, color: '#f59e0b' };
      // Nothing above names this reason. One shared rule decides whether that
      // is a broker question we have no title for or a hold of our own (R18
      // D2); an unknown reason is a hold.
      return { icon: MessageCircle, label: holdFraming.label, color: '#f59e0b' };
    }
    if (isCallRequested) return { icon: Phone, label: 'Call Requested', color: '#8b5cf6' };
    if (isWrongContact) return { icon: UserX, label: 'Contact Redirect', color: '#6b7280' };
    if (isPropertyIssue) return { icon: AlertTriangle, label: 'Property Issue', color: '#ef4444' };
    return { icon: MessageCircle, label: 'Reply Needed', color: '#3b82f6' };
  };

  const reasonInfo = getReasonInfo();
  const ReasonIcon = reasonInfo.icon;

  // What the card actually says, and which file it is about. Both were written
  // by the server and neither reached the screen: the advisor was told to
  // review a file that was never named (DEF-11).
  const cardDetails = useMemo(() => getActionCardDetails(notification), [notification]);
  const cardAttachments = useMemo(() => getActionCardAttachments(notification), [notification]);

  // Fetch user's email signature
  useEffect(() => {
    async function fetchSignature() {
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setEmailSignature(data.emailSignature || null);
          setSignatureMode(data.signatureMode || null);
          setProfessionalSignature(data.professionalSignature || null);
        }
      } catch (err) {
        console.error('Failed to fetch signature:', err);
      }
    }
    fetchSignature();
  }, []);

  const previewSignature = useMemo(() => buildActiveSignatureFromProfile({
    emailSignature,
    signatureMode,
    professionalSignature,
    email: auth.currentUser?.email || ''
  }), [emailSignature, signatureMode, professionalSignature]);

  // Initialize from notification
  useEffect(() => {
    if (!notification) return;

    // Compute recipients first
    const suggestedEmail = meta.suggestedEmail;
    let recipientList = [];
    if (needsRedirectRecipient) {
      // Keep a required input available without falling back to the original broker.
      recipientList = [''];
    } else if (suggestedEmail?.to) {
      recipientList = Array.isArray(suggestedEmail.to) ? suggestedEmail.to : [suggestedEmail.to];
    } else if (notification.email) {
      recipientList = [notification.email];
    } else {
      recipientList = normalizeRecipientList(identityThread?.email).slice(0, 1);
    }
    setRecipients(recipientList);
    setCcRecipients(getSuggestedCcRecipients(meta, recipientList));

    // Set subject
    setSubject(getReplySubject(thread, notification, suggestedEmail));

    // Set body - use suggested email if available, otherwise generate a default template
    let emailBody = '';

    if (reason === 'needs_user_input:reply_withheld_broker_promised_fields'
        && typeof meta.withheldDraft === 'string' && meta.withheldDraft.length > 0) {
      emailBody = meta.withheldDraft;
    } else if (suggestedEmail?.body) {
      emailBody = normalizeScriptWithPlaceholder(suggestedEmail.body);
    } else {
      // Generate default response template based on escalation type
      const subReason = reason.split(':')[1] || '';

      if (isNeedsUserInput) {
        if (subReason === 'email_not_delivered' || subReason === 'email_delayed') {
          // Delivery reports carry no reply draft; the advisor writes any retry.
          emailBody = '';
        } else if (subReason === 'confidential') {
          // Identity question - provide a deflecting response
          emailBody = `Hi [NAME],

Thanks for sending this over. The client prefers to remain confidential at this stage, but they are actively reviewing industrial options in the area.

Please send over the floor plan or any remaining materials you have available, and I'll follow up if they want to take the next step.

Thanks!`;
        } else if (subReason === 'client_question') {
          // Requirements question - acknowledge and provide general info
          emailBody = `Hi [NAME],

Thanks for the question. My client is evaluating options in this area, and I'll confirm the specific requirement details with them.

In the meantime, please send any remaining materials you have available, and I'll follow up if it looks like a fit.

Thanks!`;
        } else if (subReason === 'negotiation') {
          // Negotiation - acknowledge and defer
          emailBody = `Hi [NAME],

Thank you for the information. I'll discuss this with my client and get back to you.

[Add your response here]

Best regards`;
        } else if (subReason === 'legal_contract') {
          // Legal/contract - defer appropriately
          emailBody = `Hi [NAME],

Thank you for sending this over. I'll need to review this with my client before we can proceed.

[Add your response here]

Best regards`;
        } else {
          // Generic needs_user_input
          emailBody = `Hi [NAME],

Thank you for your message.

[Add your response here]

Best regards`;
        }
      } else if (isPropertyIssue) {
        const issue = meta.issue || 'the issue you mentioned';
        const severity = subReason || 'major';

        if (severity === 'critical') {
          emailBody = `Hi [NAME],

Thank you for letting me know about ${issue}. This is definitely something my client will need to consider carefully.

Could you provide more details about the current status and any remediation that has been done or is planned?

Thanks`;
        } else if (severity === 'major') {
          emailBody = `Hi [NAME],

Thank you for the heads up about ${issue}. I appreciate the transparency.

Could you provide any additional details about this? Has anything been done to address it?

Thanks`;
        } else {
          // Minor issue - just acknowledge
          emailBody = `Hi [NAME],

Thank you for mentioning ${issue}. I'll note this for my client.

Could you also confirm the other property specifications when you have a chance?

Thanks`;
        }
      } else if (isWrongContact) {
        if (meta.suggestedContact && meta.suggestedEmail) {
          // We have a new contact - draft email to them
          emailBody = `Hi [NAME],

I was referred to you by ${meta.originalContact || 'your colleague'} regarding the property at ${replyRowAnchor || 'your listing'}.

I represent a client looking for warehouse/industrial space in the area. Could you please share the property details including available SF, asking rent, and specifications?

Thank you!`;
        } else {
          // No new contact info
          emailBody = `Hi [NAME],

Thank you for letting me know. Could you please direct me to the right person to contact regarding this property?

Thanks`;
        }
      } else {
        // Fallback template
        emailBody = `Hi [NAME],

Thank you for your message.

[Add your response here]

Best regards`;
      }
    }

    // Inject broker name - use the same source as contactFirstName
    if (emailBody && brokerFullName) {
      emailBody = injectNameIntoScript(emailBody, brokerFullName);
    }

    if (emailBody) {
      setBody(stripSignOff(emailBody));
    } else if (initializedNotificationIdRef.current !== notification.id
        && (reason === 'needs_user_input:email_not_delivered' || reason === 'needs_user_input:email_delayed')) {
      // Clear a previous card's draft, but keep edits when this card refreshes.
      setBody('');
    }
    initializedNotificationIdRef.current = notification.id;

    // Initialize AI chat with proactive message based on reason
    // A hold often carries only the backend's advisor-facing sentence, so this
    // opener is no longer gated on a question the broker may never have asked.
    if (meta.question || (isNeedsUserInput && meta.details)) {
      let proactiveMessage = '';
      if (isNeedsUserInput) {
        // The five genuine reasons keep "The broker asked" and the quoted
        // sentence; every other reason - and every reason this build has never
        // seen - is framed as a hold and prefers meta.details (R18 D2).
        proactiveMessage = getHoldFraming(meta).chatMessage || '';
      } else if (isPropertyIssue) {
        const severity = reason.split(':')[1] || 'major';
        const issue = meta.issue || 'Unknown issue';
        if (severity === 'critical') {
          proactiveMessage = `⚠️ **Critical Issue Reported:**\n\n"${issue}"\n\nThis may be a health/safety concern. How would you like to proceed?`;
        } else if (severity === 'major') {
          proactiveMessage = `⚠️ **Property Issue Reported:**\n\n"${issue}"\n\nWould you like me to draft a response asking for more details, or would you prefer to address this differently?`;
        } else {
          proactiveMessage = `ℹ️ **Minor Issue Noted:**\n\n"${issue}"\n\nWould you like to acknowledge this or ask follow-up questions?`;
        }
      } else if (isWrongContact) {
        if (meta.suggestedContact) {
          proactiveMessage = `The broker indicated this isn't the right contact.\n\nSuggested contact: **${meta.suggestedContact}**${meta.suggestedEmail ? ` (${meta.suggestedEmail})` : ''}\n\nWould you like me to draft an email to the new contact?`;
        } else {
          proactiveMessage = `The broker indicated this isn't the right contact for this property.\n\nHow would you like to proceed?`;
        }
      }

      if (proactiveMessage) {
        setChatMessages([{ role: 'assistant', content: proactiveMessage }]);
      }
    }
  }, [notification, thread, identityThread, client, meta, reason, isNeedsUserInput, isPropertyIssue, isWrongContact, needsRedirectRecipient, brokerFullName, replyRowAnchor]);

  // Auto-resize textarea to fit content
  const autoResizeTextarea = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  };

  // Resize on body change
  useEffect(() => {
    autoResizeTextarea();
  }, [body]);

  // Scroll chat to bottom
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Handle body change - just set the value, no auto-manipulation
  const handleBodyChange = (newValue) => {
    setBody(newValue);
    if (draftErrors.length > 0) {
      setDraftErrors([]);
    }
  };

  // AI Chat
  const sendChatMessage = async () => {
    if (!chatInput.trim() || isChatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsChatLoading(true);

    try {
      const functions = getFunctions();
      const chatFn = httpsCallable(functions, 'chatWithPropertyContext');

      const result = await chatFn({
        message: userMessage,
        propertyInfo: {
          address: replyRowAnchor || thread?.subject,
          contactName: canonicalContactName
        },
        conversationContext: meta.conversationContext || {},
        currentEmailDraft: body,
        clientCriteria: client?.criteria || "",
        chatHistory: chatMessages.slice(-6),
        notificationContext: {
          reason: reason,
          question: meta.question,
          isNeedsUserInput
        }
      });

      const assistantMessage = result.data?.message || 'I encountered an issue. Please try again.';
      const updatedEmail = result.data?.updatedEmail;

      setChatMessages(prev => [...prev, { role: 'assistant', content: assistantMessage }]);

      if (updatedEmail) {
        setBody(updatedEmail);
        setShowEmailUpdatedBadge(true);
        setTimeout(() => setShowEmailUpdatedBadge(false), 3000);
        // Scroll to the textarea to show the updated email
        setTimeout(() => {
          textareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    } catch (err) {
      console.error('Chat error:', err);
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.'
      }]);
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  // Quick suggestions
  const quickSuggestions = [
    'Make it shorter',
    'More formal tone',
    'Add urgency'
  ];

  // Send reply - optimistic UI: close form immediately, process in background
  const handleSend = async () => {
    const cleanedRecipients = normalizeRecipientList(recipients);
    const cleanedCcRecipients = normalizeRecipientList(ccRecipients)
      .filter(email => !cleanedRecipients.some(recipient => recipient.toLowerCase() === email.toLowerCase()));
    const suggestedCcRecipients = getSuggestedCcRecipients(meta, cleanedRecipients);

    // Double-check with ref to prevent duplicate submissions across re-renders
    if (sendInProgressRef.current || isSending || !body.trim() || cleanedRecipients.length === 0) return;

    const draftValidation = validateOutboundDraft(body);
    if (!draftValidation.ok) {
      setDraftErrors(draftValidation.errors);
      return;
    }

    const threadId = notification?.threadId || thread?.threadIds?.[0] || null;
    const sourceMessageIdentity = getSourceMessageIdentity(meta);
    const { replyToMessageId } = sourceMessageIdentity;
    if (threadId && !replyToMessageId) {
      setDraftErrors([
        'This action is missing the original email needed to send a safe threaded reply. Refresh the campaign and try again, or mark it handled if no reply is needed.'
      ]);
      return;
    }

    sendInProgressRef.current = true;
    setIsSending(true);
    setDraftErrors([]);

    const uid = auth.currentUser?.uid;
    if (!uid) {
      console.error('Not signed in');
      sendInProgressRef.current = false;
      setIsSending(false);
      return;
    }

    // Use the body exactly as the user typed it - don't normalize or replace names
    const finalBody = stripSignOff(body.trim());

    // Prepare email data
    const personalizedEmails = cleanedRecipients.map(email => {
      return {
        email,
        script: finalBody,
        contactName: canonicalContactName
      };
    });

    const queuedReplyBatch = prepareActionAuditOutboxBatch({
      uid,
      entries: personalizedEmails.map(emailData => ({
        audit: {
          actionType: isWrongContact ? 'contact_redirect' : 'reply',
          client,
          notification,
          thread,
          subject,
          suggestedBody: meta.suggestedEmail?.body || '',
          suggestedRecipients: meta.suggestedEmail?.to || [],
          suggestedCcRecipients,
          finalBody: emailData.script,
          finalRecipients: [emailData.email],
          finalCcRecipients: cleanedCcRecipients,
          rowAnchor: replyRowAnchor,
          rowNumber: replyRowNumber,
          threadId,
          source: isWrongContact ? 'dashboard_contact_redirect' : 'dashboard_inline_reply',
          reason: reason || null,
          ...sourceMessageIdentity
        },
        outbox: {
          assignedEmails: [emailData.email],
          ccEmails: cleanedCcRecipients,
          script: emailData.script,
          clientId: client.id,
          subject,
          contactName: emailData.contactName,
          ...(replyRowNumber !== null ? { rowNumber: replyRowNumber } : {}),
          isPersonalized: true,
          createdAt: serverTimestamp(),
          threadId,
          replyToMessageId,
          sourceMessageId: sourceMessageIdentity.sourceMessageId,
          sourceGraphMessageId: sourceMessageIdentity.sourceGraphMessageId,
          sourceInternetMessageId: sourceMessageIdentity.sourceInternetMessageId,
          sourceMessage: sourceMessageIdentity.sourceMessage,
          notificationId: deadLetterId ? null : (notification?.id || null),
          notificationClientId: client.id,
          deleteNotificationOnSend: Boolean(notification?.id && !deadLetterId),
          sourceDeadLetterId: deadLetterId,
          resumeThreadOnSend: Boolean(threadId) && !isWrongContact,
          scriptSelectionMode: 'exact',
          forceScript: true,
          actionReason: reason || null,
          ...(isWrongContact ? {
            sourceThreadDisposition: 'contact_redirected',
            redirectContext: {
              sourceThreadId: threadId,
              sourceNotificationId: notification?.id || null,
              sourceGraphMessageId: sourceMessageIdentity.sourceGraphMessageId,
              sourceInternetMessageId: sourceMessageIdentity.sourceInternetMessageId,
              targetEmail: emailData.email,
              targetContactName: emailData.contactName,
              reason: reason || null
            }
          } : {})
        }
      }))
    });

    const maxRetries = 3;
    for (let attempt = 1; attempt <= maxRetries; attempt += 1) {
      try {
        await queuedReplyBatch.commit();
        onSend?.();
        return;
      } catch (err) {
        console.error(`Outbox write attempt ${attempt} failed:`, err);
        if (attempt === maxRetries) {
          setDraftErrors(['Could not queue this reply. Please try again.']);
        } else {
          await new Promise(r => setTimeout(r, 1000 * attempt));
        }
      }
    }

    sendInProgressRef.current = false;
    setIsSending(false);
  };

  const canSend = body.trim().length > 0 && recipients.some(email => email.trim()) && !isSending && !sendDisabledReason;

  return (
    <div className="inline-reply-composer">
      {/* Header */}
      <div className="irc-header">
        <div className="irc-header-left">
          <ReasonIcon size={18} style={{ color: reasonInfo.color }} />
          <span className="irc-header-title">{reasonInfo.label}</span>
        </div>
        <button className="irc-close-btn" onClick={onDismiss} title="Close reply composer" aria-label="Close reply composer">
          <X size={16} />
        </button>
      </div>

      {/* The card's own explanation, verbatim, and the file it is about. A
          link is offered only when the card carries a real location - while
          publication is unproved the name is text, not a dead href. */}
      {(cardDetails || cardAttachments.length > 0) && (
        <div className="irc-card-context">
          {cardDetails && <p className="irc-card-details">{cardDetails}</p>}
          {cardAttachments.length > 0 && (
            <ul className="irc-card-attachments">
              {cardAttachments.map(attachment => (
                <li key={attachment.name} className="irc-card-attachment">
                  <FileText size={14} aria-hidden="true" />
                  {attachment.url ? (
                    <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                      {attachment.name}
                    </a>
                  ) : (
                    <span>{attachment.name}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {cardAttachments.some(attachment => !attachment.url) && (
            <p className="irc-card-details">
              To review a file without a link, open the original broker message in your connected mailbox.
            </p>
          )}
        </div>
      )}

      {/* Broker's question is visible in the message above - no need to repeat */}

      {/* Composer */}
      <div className="irc-composer">
        {/* Subject is auto-set for thread replies - no need to show/edit */}

        {/* Body */}
        <div className="irc-field irc-field-body">
          <div className="irc-body-header">
            <label className="irc-label">Message</label>
            <button
              className={`irc-ai-btn ${showAiChat ? 'active' : ''}`}
              onClick={() => setShowAiChat(!showAiChat)}
            >
              <Sparkles size={14} />
              {showAiChat ? 'Hide AI' : 'AI Help'}
            </button>
          </div>

          {/* AI Chat Panel - inline above textarea */}
          {showAiChat && (
            <div className="irc-chat-panel">
              <div className="irc-chat-header">
                <Bot size={16} />
                <span>AI Assistant</span>
              </div>

              <div className="irc-chat-messages" ref={chatMessagesRef}>
                {chatMessages.length === 0 ? (
                  <div className="irc-chat-welcome">
                    <p>Ask me to help draft or refine your reply.</p>
                    <div className="irc-quick-suggestions">
                      {quickSuggestions.map((suggestion, i) => (
                        <button
                          key={i}
                          className="irc-quick-btn"
                          onClick={() => {
                            setChatInput(suggestion);
                            chatInputRef.current?.focus();
                          }}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  chatMessages.map((msg, i) => (
                    <div key={i} className={`irc-chat-message irc-chat-${msg.role}`}>
                      <div className="irc-chat-bubble">{msg.content}</div>
                    </div>
                  ))
                )}
                {isChatLoading && (
                  <div className="irc-chat-message irc-chat-assistant">
                    <div className="irc-chat-bubble irc-chat-loading">
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                )}
              </div>

              <div className="irc-chat-input-container">
                <input
                  ref={chatInputRef}
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={handleChatKeyDown}
                  placeholder="Ask AI for help..."
                  className="irc-chat-input"
                  disabled={isChatLoading}
                />
                <button
                  onClick={sendChatMessage}
                  disabled={!chatInput.trim() || isChatLoading}
                  className="irc-chat-send"
                >
                  {isChatLoading ? <RefreshCw size={14} className="spinning" /> : <Send size={14} />}
                </button>
              </div>
            </div>
          )}

          <textarea
            ref={textareaRef}
            value={body}
            onChange={(e) => {
              handleBodyChange(e.target.value);
              autoResizeTextarea();
            }}
            className="irc-textarea"
            placeholder={contactFirstName ? `Hi ${contactFirstName},\n\nEnter your reply...` : "Hi,\n\nEnter your reply..."}
          />

          {/* Email Updated Badge */}
          {showEmailUpdatedBadge && (
            <div className="irc-updated-badge">
              <Check size={12} />
              Email updated by AI
            </div>
          )}
          {draftErrors.length > 0 && (
            <div className="irc-draft-errors" role="alert">
              {draftErrors.map(error => (
                <div key={error}>{error}</div>
              ))}
            </div>
          )}
        </div>

        {/* Signature Preview */}
        <SignaturePreview
          signatureMode={signatureMode}
          emailSignature={previewSignature}
          isLoading={false}
          compact={true}
        />

        {/* Recipients */}
        <div className="irc-recipients">
          <Mail size={14} />
          <span>To:</span>
          <div className="irc-recipient-list">
            {recipients.map((email, i) => (
              <input
                key={i}
                type="email"
                required={needsRedirectRecipient}
                value={email}
                onChange={(e) => {
                  const nextRecipients = [...recipients];
                  nextRecipients[i] = e.target.value;
                  setRecipients(nextRecipients);
                }}
                className="irc-recipient-input"
                aria-label={`Recipient ${i + 1}`}
                placeholder="broker@email.com"
              />
            ))}
          </div>
        </div>
        {ccRecipients.length > 0 && (
          <div className="irc-recipients irc-cc-recipients">
            <Mail size={14} />
            <span>CC:</span>
            <div className="irc-recipient-list">
              {ccRecipients.map((email, i) => (
                <span key={`${email}-${i}`} className="irc-recipient-chip irc-cc-chip">
                  {email}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {needsRedirectRecipient && (
        <p className="irc-card-details">
          The broker named {cleanContactName(meta.suggestedEmail?.contactName || meta.suggestedContact) || 'a colleague'} but gave no contact details — add an address to send this draft.
        </p>
      )}

      {sendDisabledReason && (
        <p className="irc-send-disabled-reason" role="status">{sendDisabledReason}</p>
      )}

      {/* Actions */}
      <div className="irc-actions">
        <button className="irc-btn irc-btn-cancel" onClick={onDismiss}>
          Cancel
        </button>
        {canMarkHandled && (
          <button className="irc-btn irc-btn-resolve" onClick={onResolve}>
            <Check size={14} />
            Mark Handled
          </button>
        )}
        <button
          className={`irc-btn irc-btn-send ${isSending ? 'sending' : ''}`}
          onClick={handleSend}
          disabled={!canSend}
        >
          {isSending ? (
            <>
              <RefreshCw size={14} className="spinning" />
              Sending...
            </>
          ) : (
            <>
              <Send size={14} />
              Send Reply
            </>
          )}
        </button>
      </div>
    </div>
  );
}
